import { listingPlace } from './listingPlace';

/**
 * Utility functions to transform GasWork listing data to RxRooster listing format
 * This allows reuse of existing FlippableListingCard components
 */

/**
 * Transform a GasWork listing to RxRooster listing format
 * @param {Object} gasworkListing - GasWork listing from backend
 * @returns {Object} Transformed listing compatible with FlippableListingCard
 */
export const transformGasWorkListingToRxFormat = (gasworkListing) => {
  if (!gasworkListing) return null;

  // Extract compensation values
  const minIncome = gasworkListing.minIncome || 0;
  const maxIncome = gasworkListing.maxIncome || null;

  const providedCompensation =
    gasworkListing.compensation &&
    typeof gasworkListing.compensation === 'object' &&
    !Array.isArray(gasworkListing.compensation)
      ? gasworkListing.compensation
      : {};
  const rateWithheld =
    providedCompensation.isBlind === true ||
    providedCompensation.isCompBlind === true;

  // Do not derive displayable compensation when it is absent or withheld.
  const isBlindListing = rateWithheld || (minIncome === 0 && !maxIncome);

  // Determine if compensation is salary or hourly based on value
  // If amount > 500, treat as annual salary, otherwise hourly rate
  const isSalary = minIncome > 500 || (maxIncome && maxIncome > 500);

  // Public normalized-listing shares deliberately keep the visible rate in
  // minIncome/maxIncome while the compensation object carries only metadata
  // such as blindness and contract type. Treat that object as a partial
  // overlay, not a replacement for the derived rate fields. A truthy
  // metadata-only object previously discarded the $290 hourly amount and left
  // ExternalListingFront to misread 290 as an annual salary ($0K/yr).
  const derivedCompensation = {
    type: isBlindListing ? [] : (isSalary ? ['Salary'] : ['Hourly']),
    hourlyAmount: isBlindListing || isSalary ? 0 : minIncome,
    maxHourlyAmount: isBlindListing || isSalary ? 0 : maxIncome,
    annualAmount: isBlindListing || !isSalary ? 0 : minIncome,
    maxAnnualAmount: isBlindListing || !isSalary ? 0 : maxIncome,
    weeklyAmount: 0,
    period: isSalary ? 'annual' : 'hourly',
    includesHousing: false,
    includesTravel: false,
    malpracticeCovered: false,
    contractType: gasworkListing.duration || '1099',
    hasCompensation: !isBlindListing,
  };
  // listingPlace owns this rule now. It already handled the 'Unknown' city this
  // line was written for, and also drops a city that IS its own state -- the
  // same non-fact, arriving from ingestion for a posting that names no city.
  const locationString = listingPlace(
    gasworkListing.city,
    gasworkListing.state,
  ).location;

  return {
    // IDs
    _id: gasworkListing._id,
    id: gasworkListing._id,

    // Facility Information
    facilityName: locationString,
    facilityType: gasworkListing.section === 'CRNA' ? 'hospital' : 'hospital',

    // Location
    location: locationString, // Use string format for compatibility with ListingFront component
    city: gasworkListing.city,
    state: gasworkListing.state,
    geoLocation: gasworkListing.geoLocation,

    // Comp-blind market band (top-level from the sanitized backend response) so the
    // unified Competitive Rate badge has the market range to reveal on tap.
    marketBand: gasworkListing.marketBand || undefined,

    // Pre-extracted fields remain authoritative, while absent standard fields
    // are hydrated from the public minIncome/maxIncome contract above.
    compensation: {
      ...derivedCompensation,
      ...providedCompensation,
    },

    // Assignment Details - use duration field for assignment type (e.g., 'Locum Tenens', 'Full Time')
    assignment: {
      types: gasworkListing.duration ? [gasworkListing.duration] : [],
      startDate: gasworkListing.datePosted,
      duration: gasworkListing.duration,
    },

    // Schedule (minimal info for GasWork)
    schedule: {
      pattern: 'Not specified',
      hoursPerWeek: null,
    },

    // Description
    title: gasworkListing.briefDescription,
    summary: gasworkListing.briefDescription,
    description: gasworkListing.fullDescription || gasworkListing.briefDescription,

    // Clinical Info
    clinical: {
      specialties: [],
      caseTypes: [],
    },
    clinicalExpectations: {
      emrSystem: null, // Not available for external listings
    },

    // Website - removed for privacy (external listings)
    website: null,

    // Public source/apply links. The modal validates the scheme again before
    // rendering an href because these values originate in scraped content.
    sourceUrl: gasworkListing.sourceUrl || gasworkListing.url || gasworkListing.inquireLink || '',
    url: gasworkListing.url || '',
    inquireLink: gasworkListing.inquireLink || '',

    // Contact - removed for privacy (external listings)
    contact: {},

    // Metadata
    providerType: gasworkListing.section,
    reviewStatus: gasworkListing.status === 'Live' ? 'reviewed' : 'pending',
    createdAt: gasworkListing.datePosted,
    updatedAt: gasworkListing.lastSyncedAt,
    tags: ['GasWork', gasworkListing.section, gasworkListing.state],

    // Source tracking - use 'external' for consistency
    sourceType: 'external',
    sourceListingId: gasworkListing.externalId,

    // Share link data
    shareToken: gasworkListing.shareToken,
    shareUrl: gasworkListing.shareToken
      ? `${window.location.origin}/g/${gasworkListing.shareToken}`
      : null,

    // Additional external listing metadata (no PII)
    externalMeta: {
      priorityLevel: gasworkListing.priorityLevel,
      w2Status: gasworkListing.w2Status,
    },

    // NEW: Fields needed for ExternalListingFront/Back components
    // These are always available per MongoDB audit
    section: gasworkListing.section, // CRNA or Anesthesiologist
    briefDescription: gasworkListing.briefDescription,
    fullDescription: gasworkListing.fullDescription || gasworkListing.briefDescription,
    duration: gasworkListing.duration, // Always available: "Full Time", "Locum Tenens", etc.
    datePosted: gasworkListing.datePosted, // Always available
    priorityLevel: gasworkListing.priorityLevel || 'General',
    
    // Raw compensation values for ExternalListingFront formatting
    minIncome: gasworkListing.minIncome || 0,
    maxIncome: gasworkListing.maxIncome || 0,
    
    // Match metadata (passed through from backend)
    _matchScore: gasworkListing._matchScore,
    _distance: gasworkListing._distance,
    _isBlindListing: gasworkListing._isBlindListing,
    
    // Contact info availability flags (for external listings)
    // Use backend flags directly if available, otherwise derive from contact fields
    _hasPhone: gasworkListing._hasPhone || !!(gasworkListing.contactPhone),
    _hasEmail: gasworkListing._hasEmail || !!(gasworkListing.contactEmail),
    _hasName: gasworkListing._hasName || !!(gasworkListing.contactName),
    _hasContactInfo: gasworkListing._hasContactInfo || gasworkListing.hasContactInfo || !!(gasworkListing.contactName || gasworkListing.contactEmail || gasworkListing.contactPhone),
    
    // Raw contact info fields from backend (may be null for privacy)
    contactName: gasworkListing.contactName || null,
    contactEmail: gasworkListing.contactEmail || null,
    contactPhone: gasworkListing.contactPhone || null,
    hasContactInfo: gasworkListing._hasContactInfo || gasworkListing.hasContactInfo || !!(gasworkListing.contactName || gasworkListing.contactEmail || gasworkListing.contactPhone),

    // Facility details structure for compatibility
    facilityDetails: {
      location: {
        city: gasworkListing.city,
        state: gasworkListing.state,
      },
      facilityType: gasworkListing.section === 'CRNA' ? 'hospital' : 'hospital',
      clinicalExpectations: {
        emrSystem: null,
      },
    },
  };
};

/**
 * Transform multiple GasWork listings
 * @param {Array} listings - Array of GasWork listings
 * @returns {Array} Transformed listings
 */
export const transformGasWorkListings = (listings) => {
  if (!Array.isArray(listings)) return [];
  return listings.map(transformGasWorkListingToRxFormat).filter(Boolean);
};

/**
 * Check if a listing is from GasWork
 * @param {Object} listing - Listing object
 * @returns {boolean} True if from GasWork
 */
export const isGasWorkListing = (listing) => {
  return listing?.sourceType === 'gaswork' || listing?.gaswork !== undefined;
};

/**
 * Get a user-friendly display name for compensation
 * @param {Object} gasworkListing - GasWork listing
 * @returns {string} Formatted compensation string
 */
export const formatGasWorkCompensation = (gasworkListing) => {
  const min = gasworkListing.minIncome;
  const max = gasworkListing.maxIncome;

  if (!min && !max) return 'Compensation not specified';
  
  // Determine if salary or hourly based on value (> 500 = salary)
  const isSalary = min > 500 || (max && max > 500);
  
  if (isSalary) {
    // Format as annual salary
    const minK = min ? `$${Math.round(min / 1000)}K` : '';
    const maxK = max ? `$${Math.round(max / 1000)}K` : '';
    
    if (min && max) return `${minK} - ${maxK}/yr`;
    if (min) return `${minK}+/yr`;
    if (max) return `Up to ${maxK}/yr`;
  } else {
    // Format as hourly rate
    if (min && max) return `$${min.toLocaleString()} - $${max.toLocaleString()}/hr`;
    if (min) return `$${min.toLocaleString()}+/hr`;
    if (max) return `Up to $${max.toLocaleString()}/hr`;
  }
  
  return 'Compensation not specified';
};

export default {
  transformGasWorkListingToRxFormat,
  transformGasWorkListings,
  isGasWorkListing,
  formatGasWorkCompensation,
};
